import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../assets/styles/AuthForm.css';
import { authService } from '../../services/authService';
import { validateSignUpForm } from '../../utils/validators';
import { ValidationErrors, SignUpData } from '../../types/auth.types';
import Footer from '../../components/Layout/Footer';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('link');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userRole] = useState(7);
  const [shift] = useState('1');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  useEffect(() => {
    if (!token) {
      setError('Invalid or missing registration link');
      return;
    }

    const fetchEmail = async () => {
      try {
        const res = await authService.decodeInvite(token);
        if (res?.data?.status === 'success') {
          setEmail(res?.data?.email);
        } else {
          setError(res?.data?.message || 'Invalid or expired registration link');
        }
      } catch {
        setError('Invalid or expired registration link');
      }
    };

    fetchEmail();
  }, []);

  // 🔹 Validate live (used onChange)
  const runValidation = (
    updatedValues?: Partial<{
      name: string;
      email: string;
      password: string;
      confirmPassword: string;
    }>
  ) => {
    const errors = validateSignUpForm(
      updatedValues?.name ?? name,
      updatedValues?.email ?? email,
      updatedValues?.password ?? password,
      updatedValues?.confirmPassword ?? confirmPassword,
      userRole,
      shift
    );

    setValidationErrors(errors);
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    runValidation();
  };

  const getFieldClassName = (field: string) =>
    touched[field] && validationErrors[field]
      ? 'form-group has-error'
      : 'form-group';

  const renderFieldError = (field: string) =>
    touched[field] && validationErrors[field] ? (
      <span className="field-error">{validationErrors[field]}</span>
    ) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateSignUpForm(
      name,
      email,
      password,
      confirmPassword,
      userRole,
      shift
    );

    setValidationErrors(errors);
    setTouched({
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
    });

    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError(null);

    const payload: SignUpData = {
      first_name: name,
      email: email.toLowerCase() ?? '',
      password,
      confirm_password: confirmPassword,
      role_id: userRole,
      shift,
    };

    try {
      const response = await authService.signUp(payload, token);
      // if (response.role_id !== 6) {
      if (response.role_id !== 7) {
        setError('You dont have a permission to login, kindly contact admin.');
        return
      }
      // const redirectPath = response.role_id === 1 ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD;
      localStorage.setItem("accessToken", response.access_token);
      localStorage.setItem("refreshToken", response.refresh_token || "");
      localStorage.setItem("role_id", response.role_id.toString());
      localStorage.setItem("email", response.email);
      localStorage.setItem("permissions", JSON.stringify(response.permissions));
      localStorage.setItem("user", JSON.stringify(response));
      localStorage.setItem("name", response?.first_name);
      localStorage.setItem('last_name', response?.last_name || "");
      localStorage.setItem('phone_number', response?.phone_number || "");
      localStorage.setItem('job_title', response?.job_title || "");
      localStorage.setItem('linkedin_id', response?.linkedin_id || "");
      localStorage.setItem('facebook_id', response?.facebook_id || "");
      localStorage.setItem('twitter_id', response?.twitter_id || "");
      localStorage.setItem('instagram_id', response?.instagram_id || "");
      localStorage.setItem('profile_pic', response?.profile_pic || "");
      localStorage.setItem('is_sub_user', String(response?.is_sub_user || false));

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-form-container">
        <div className="auth-logos">
            <img src="/images/cc-spokesly-logo.png" alt="Main Logo"   style={{ height: "63px" }} />
        </div>
        <div className="auth-form-wrapper">
          <form className="auth-form" onSubmit={handleSubmit}>
            <h2>Sign up</h2>

            <div className="form-row">
              <div className={getFieldClassName('name')}>
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    runValidation({ name: e.target.value });
                  }}
                  onBlur={() => handleBlur('name')}
                />
                {renderFieldError('name')}
              </div>

              <div className={getFieldClassName('email')}>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  placeholder="Email address"
                  value={email}
                  readOnly={true}
                />
                {renderFieldError('email')}
              </div>
            </div>

            <div className="form-row">
              <div className={getFieldClassName('password')}>
                <label htmlFor="password">Set password</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      runValidation({ password: e.target.value });
                    }}
                    onBlur={() => handleBlur('password')}
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'hide' : 'show'}
                  </span>
                </div>
                {renderFieldError('password')}
              </div>

              <div className={getFieldClassName('confirmPassword')}>
                <label htmlFor="confirmPassword">Confirm password</label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    placeholder="Re-write Password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      runValidation({ confirmPassword: e.target.value });
                    }}
                    onBlur={() => handleBlur('confirmPassword')}
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? 'hide' : 'show'}
                  </span>
                </div>
                {renderFieldError('confirmPassword')}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Loading...
                </>
              ) : (
                'Sign-up'
              )}
            </button>

            <div className="form-footer">
              <p>
                Already have an account?{' '}
                <a href="/signin" className="form-footer-link">
                  Sign In
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUpPage;
